import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import {
  withUser,
  withBonuses,
  withBonusActions,
  withCoreComponent,
  withFormData,
} from 'core/hocs';
import { USER_FIELDS } from 'core/constants';
import {
  AllBonuses as AllBonusesCore,
  RouteLink
} from 'core/components';

import './registration-bonus.scss';
import * as R from 'ramda';
import { isEmptyOrNil } from 'core/helpers';

import classNames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { formatRegistrationDate, getRegistrationBonusesData } from '../../helpers/utils';
import { RegistrationBonusView } from './registration-bonus-view/registration-bonus-view';
import { HeaderButtons } from './header-buttons/header-buttons';
import { TopBarContext } from '../../hocs/with-top-bar';
import { parseJson } from '../../helpers/json';

const findActiveBonus = bonuses => bonuses.reduce(
  (a, b) => a.registrationExtendedExpiryDate < b.registrationExtendedExpiryDate ? a : b,
  []
);

const filterOutdatedBonuses = bonuses => bonuses.filter(({ registrationExtendedExpiryDate, hasNoExpiry }) => {
  const now = new Date();

  return (registrationExtendedExpiryDate > now || hasNoExpiry);
});

const filterOutActivatedBonuses = (activeRegistrationBonus, allBonuses) => {
  const bonus = (allBonuses || []).find(el => el.bonusPlanId === activeRegistrationBonus.bonusPlanId);

  return bonus?.status ? null : activeRegistrationBonus;
};

const formatBonuses = (depositBonusesItems, intl, regDate) => {
  const targetDepositBonuses = (depositBonusesItems || []).reduce((acc, item) => {
    const { bonusAmount, startDate, endDate } = item || {};
    const registrationExtendedExpiryDate = formatRegistrationDate(regDate);
    const bonusData = getRegistrationBonusesData(item.bonusPlanId, intl);

    if (bonusData?.endHours) {
      registrationExtendedExpiryDate.setHours(registrationExtendedExpiryDate.getHours() + bonusData?.endHours);

      return [...acc, {
        ...R.omit(['startDate', 'endDate', 'bonusAmount'], item),
        amount: bonusAmount,
        expiryDate: endDate,
        triggerDate: startDate,
        registrationExtendedExpiryDate,
        hasNoExpiry: bonusData?.hasNoExpiry,
      }];
    }

    return acc;
  }, []);

  return targetDepositBonuses;
};

const getShowOnTheBlockBonusesIds = intl => parseJson(
  intl.formatMessage({ id: 'show-on-the-block.bonuses' }),
  'error while parsing show-on-the-block bonuses',
  []
);

const mapBonusesForBonusView = (bonusesIds = [], depositBonuses = []) => bonusesIds.map((item) => {
  const depositBonusItem = depositBonuses.find(depBonus => String(depBonus.bonusPlanId) === String(item));
  const { bonusAmount, startDate, endDate } = depositBonusItem || {};

  return {
    ...R.omit(['startDate', 'endDate', 'bonusAmount'], depositBonusItem),
    amount: bonusAmount,
    expiryDate: endDate,
    triggerDate: startDate,
  };
});

class RegistrationBonusUI extends Component {
  static propTypes = {
    userData: PropTypes.shape().isRequired, // eslint-disable-line react/no-unused-prop-types
    isInProgress: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
    depositBonuses: PropTypes.shape(), // eslint-disable-line react/no-unused-prop-types
    allBonuses: PropTypes.shape(), // eslint-disable-line react/no-unused-prop-types
    getDepositBonuses: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
    setPendingBonus: PropTypes.func.isRequired,
    getAllBonuses: PropTypes.func.isRequired,
    isHeader: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
    processDeposit: PropTypes.shape(), // eslint-disable-line react/no-unused-prop-types
    intl: intlShape.isRequired,
  };

  static contextType = TopBarContext

  static defaultProps = {
    isInProgress: false,
    isHeader: false,
    depositBonuses: {},
    allBonuses: {},
    processDeposit: {}
  };

  state = {
    bonus: null,
  };

  static getDerivedStateFromProps(props) {
    const {
      depositBonuses: { items, isInProgress },
      allBonuses,
      intl,
      userData: {
        regDate,
      },
    } = props;

    if (!isInProgress && !isEmptyOrNil(items)) {
      const activeRegistrationBonus = findActiveBonus(filterOutdatedBonuses(formatBonuses(
        items,
        intl,
        regDate,
      )));

      const showOnTheBlockBonuses = R.compose(
        bonusesIds => mapBonusesForBonusView(bonusesIds, items),
        () => getShowOnTheBlockBonusesIds(intl)
      )();

      const filteredTimeLimitedBonus = filterOutActivatedBonuses(activeRegistrationBonus, allBonuses.items);

      const filteredShowOnTheBlockBonuses = R.filter(
        item => filterOutActivatedBonuses(item, allBonuses.items),
        showOnTheBlockBonuses
      );

      return {
        bonus: isEmptyOrNil(filteredTimeLimitedBonus) ? filteredShowOnTheBlockBonuses[0] : filteredTimeLimitedBonus
      };
    }

    return null;
  }

  componentDidMount() {
    const { getAllBonuses } = this.props;

    getAllBonuses();
  }

  componentDidUpdate(prevProps) {
    const {
      processDeposit: { isInProgress: prevIsInProgress }, depositBonuses:
      { isInProgress: prevDepositIsInProgress }
    } = prevProps;
    const {
      processDeposit: { isInProgress }, depositBonuses: { items, isInProgress: depositIsInProgress },
      allBonuses,
      intl,
      userData: {
        regDate,
      },
    } = this.props;
    const { isHeader, getAllBonuses } = this.props;
    const { bonus } = this.state;
    const { isRegistrationBonusOpen, showRegistrationBonus, closeAndResetRegistrationBonus } = this.context;

    if (prevIsInProgress && !isInProgress) {
      getAllBonuses();
    }

    if (R.isNil(items) && prevDepositIsInProgress && !depositIsInProgress) {
      this.getAllDepositBonuses();
    }

    if (isRegistrationBonusOpen === null && !isEmptyOrNil(bonus) && isHeader) {
      showRegistrationBonus();
    }

    const activeRegistrationBonusByTimer = findActiveBonus(filterOutdatedBonuses(formatBonuses(
      items,
      intl,
      regDate,
    )));
    const showOnTheBlockBonuses = this.getShowOnTheBlockBonuses() || [];

    const activatedBonus = filterOutActivatedBonuses(activeRegistrationBonusByTimer, allBonuses.items);
    const filteredShowOnTheBlockBonuses = R.filter(
      item => filterOutActivatedBonuses(item, allBonuses.items),
      showOnTheBlockBonuses
    );

    if (isRegistrationBonusOpen
      && (activeRegistrationBonusByTimer || !isEmptyOrNil(showOnTheBlockBonuses))
      && isEmptyOrNil(activatedBonus)
      && isEmptyOrNil(filteredShowOnTheBlockBonuses[0])
      && isHeader
      && isEmptyOrNil(bonus)
    ) {
      closeAndResetRegistrationBonus();
    }
  }

  getAllDepositBonuses = () => {
    const { getDepositBonuses } = this.props;

    getDepositBonuses({
      depositAmount: 1000000,
      paymentMethod: 'IS_CREDITCARD_5',
    });
  };

  getShowOnTheBlockBonuses = () => {
    const {
      depositBonuses: { items },
      intl,
    } = this.props;

    const showOnTheBlockBonuses = R.compose(
      bonusesIds => mapBonusesForBonusView(bonusesIds, items || []),
      () => getShowOnTheBlockBonusesIds(intl)
    )();

    return showOnTheBlockBonuses;
  }

  handleActivateBonus = ({ currentTarget: { dataset: { id, minAmount } } }) => {
    const { setPendingBonus } = this.props;
    setPendingBonus({ bonusPlanId: id, minAmount });
  };

  render() {
    const { isHeader } = this.props;
    const { bonus } = this.state;
    const { isRegistrationBonusOpen } = this.context;

    if (isEmptyOrNil(bonus) || (!isRegistrationBonusOpen && isHeader)) {
      return null;
    }

    return (
      <RegistrationBonusView
        bonus={bonus}
        className={classNames({
          'registration-bonus-bar position-fixed p-sm-1_25 p-1_5 rounded-0 top-bar': isHeader,
          'p-2 mb-4': !isHeader
        })}
        isHeader={isHeader}
        buttons={isHeader
          ? (
            <HeaderButtons
              onGetBonus={this.handleActivateBonus}
              id={bonus.bonusPlanId}
              minAmount={bonus.description?.minAmount}
            />
          ) : (
            <Button
              tag={RouteLink}
              to="/wallet"
              onClick={this.handleActivateBonus}
              data-id={bonus.bonusPlanId}
              data-min-amount={bonus.description?.minAmount}
              className="claim-button px-2 text-primary"
              color="white"
            >
              <FormattedMessage id="gifts.registration.claim" />
            </Button>
          // eslint-disable-next-line react/jsx-indent
          )}
      />
    );
  }
}

export const RegistrationBonus = withCoreComponent(
  AllBonusesCore,
  withBonusActions(withBonuses(
    withUser(
      injectIntl(
        withFormData(RegistrationBonusUI)
      ),
      [USER_FIELDS.USER_DATA],
    )
  ))
);

